// ─── slugUtils.js ─────────────────────────────────────────────────────────────
// Converts search filters ↔ SEO-friendly URL slugs
// Pattern: /pg-[gender]-[price]-[sharing]-in-[location]-pppfr
//
// Examples:
//   { location: "Pitampura" }
//     → /pg-in-pitampura-pppfr
//
//   { location: "Karol Bagh", gender: "Boys", price: "Under ₹5,000" }
//     → /pg-boys-under-5000-in-karol-bagh-pppfr
//
//   { location: "Rohini", gender: "Girls", price: "₹5,000–₹8,000", sharing: "Double" }
//     → /pg-girls-5000-to-8000-double-in-rohini-pppfr
// ─────────────────────────────────────────────────────────────────────────────

// ── Price token maps ──────────────────────────────────────────────────────────
const PRICE_TO_SLUG = {
  'Under ₹5,000':     'under-5000',
  'Above ₹5,000':     'above-5000',
  'Under ₹6,000':     'under-6000',
  'Above ₹6,000':     'above-6000',
  'Under ₹7,000':     'under-7000',
  'Above ₹7,000':     'above-7000',
  'Under ₹8,000':     'under-8000',
  'Above ₹8,000':     'above-8000',
  'Under ₹9,000':     'under-9000',
  'Above ₹9,000':     'above-9000',
  'Under ₹10,000':    'under-10000',
  'Above ₹10,000':    'above-10000',
  'Under ₹11,000':    'under-11000',
  'Above ₹11,000':    'above-11000',
  'Under ₹12,000':    'under-12000',
  'Above ₹12,000':    'above-12000',
}

const SLUG_TO_PRICE = Object.fromEntries(
  Object.entries(PRICE_TO_SLUG).map(([k, v]) => [v, k])
)

// ── Gender token maps ─────────────────────────────────────────────────────────
const GENDER_TO_SLUG = {
  'Boys':  'boys',
  'Girls': 'girls',
  'Co-ed': 'coed',
}

const SLUG_TO_GENDER = Object.fromEntries(
  Object.entries(GENDER_TO_SLUG).map(([k, v]) => [v, k])
)

// ── Sharing token maps ────────────────────────────────────────────────────────
const SHARING_TO_SLUG = {
  'Single': 'single',
  'Double': 'double',
  'Triple': 'triple',
}

const SLUG_TO_SHARING = Object.fromEntries(
  Object.entries(SHARING_TO_SLUG).map(([k, v]) => [v, k])
)

// ── Helpers ───────────────────────────────────────────────────────────────────
/**
 * Converts a location string to a URL-safe slug fragment.
 * "Karol Bagh"  → "karol-bagh"
 * "Rohini Sec 9" → "rohini-sec-9"
 */
export const locationToSlug = (location) => {
  if (!location) return ''
  return location
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')   // strip special chars except hyphens
    .replace(/\s+/g, '-')       // spaces → dashes
    .replace(/-+/g, '-')        // collapse multiple dashes
}

/**
 * Converts a location slug fragment back to a display name.
 * "karol-bagh" → "Karol Bagh"
 */
export const slugToLocation = (slug) => {
  if (!slug) return ''
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

// ── Core encoder ─────────────────────────────────────────────────────────────
/**
 * Builds a full SEO path from filter values.
 * @param {string} location  - "Pitampura"
 * @param {string} gender    - "Boys" | "Girls" | "Co-ed" | "" | null
 * @param {string} price     - "Under ₹5,000" | ... | "" | null
 * @param {string} sharing   - "Single" | "Double" | "Triple" | "" | null
 * @returns {string} e.g. "/pg-boys-under-5000-in-pitampura-pppfr"
 */
export const buildSlugPath = (location, gender = '', price = '', sharing = '') => {
  const parts = []

  if (gender && GENDER_TO_SLUG[gender])    parts.push(GENDER_TO_SLUG[gender])
  if (price  && PRICE_TO_SLUG[price])      parts.push(PRICE_TO_SLUG[price])
  if (sharing && SHARING_TO_SLUG[sharing]) parts.push(SHARING_TO_SLUG[sharing])

  // Filter-only (no location) — e.g. /pg-single-pppfr, /pg-boys-pppfr
  if (!location) {
    if (parts.length === 0) return '/listings'
    return `/pg-${parts.join('-')}-pppfr`
  }

  // Location + optional filters — e.g. /pg-boys-in-pitampura-pppfr
  const locSlug   = locationToSlug(location)
  const filterStr = parts.length > 0 ? parts.join('-') + '-' : ''
  return `/pg-${filterStr}in-${locSlug}-pppfr`
}

// ── Core decoder ─────────────────────────────────────────────────────────────
/**
 * Parses a full pathname back into filter values.
 * Handles both the new slug format AND the legacy query-param format.
 *
 * @param {string} pathname  - e.g. "/pg-boys-under-5000-in-pitampura-pppfr"
 * @param {URLSearchParams} searchParams - for legacy ?location= support
 * @returns {{ location, gender, price, sharing }}
 */
export const parseSlugPath = (pathname, searchParams = null) => {
  // ── Legacy query-param format ──────────────────────────────────────────────
  if (pathname === '/listings' && searchParams) {
    return {
      location: searchParams.get('location') || '',
      gender:   searchParams.get('gender')   || '',
      price:    searchParams.get('price')    || '',
      sharing:  searchParams.get('sharing')  || '',
    }
  }

  // ── New slug format ────────────────────────────────────────────────────────
  // Strip leading slash and the -pppfr suffix
  const raw = pathname.replace(/^\//, '').replace(/-pppfr$/, '')

  // Must start with "pg-"
  if (!raw.startsWith('pg-')) {
    return { location: '', gender: '', price: '', sharing: '' }
  }

  const body = raw.slice(3) // remove "pg-"

  // Split on "-in-" to separate filters from location
  const inIdx = body.indexOf('-in-')
  if (inIdx === -1) {
    // Either "pg-in-{location}" (bare location) or "pg-{filters}-pppfr" (filter-only, no location)
    if (body.startsWith('in-')) {
      // bare location: pg-in-pitampura
      return { location: slugToLocation(body.slice(3)), gender: '', price: '', sharing: '' }
    }

    // Filter-only slug: parse tokens from the whole body
    let gender  = ''
    let price   = ''
    let sharing = ''
    let remaining = body

    const allTokens = [
      ...Object.values(PRICE_TO_SLUG),
      ...Object.values(GENDER_TO_SLUG),
      ...Object.values(SHARING_TO_SLUG),
    ].sort((a, b) => b.length - a.length)

    for (const token of allTokens) {
      const regex = new RegExp(`(^|-)${token}(-|$)`)
      if (regex.test(remaining)) {
        if (SLUG_TO_GENDER[token])   gender  = SLUG_TO_GENDER[token]
        if (SLUG_TO_PRICE[token])    price   = SLUG_TO_PRICE[token]
        if (SLUG_TO_SHARING[token])  sharing = SLUG_TO_SHARING[token]
        remaining = remaining.replace(regex, (_, pre, post) => (pre && post) ? '-' : '').replace(/^-|-$/g, '')
      }
    }

    return { location: '', gender, price, sharing }
  }

  const filterStr = body.slice(0, inIdx)  // e.g. "boys-under-5000-double"
  const locStr    = body.slice(inIdx + 4) // e.g. "karol-bagh"

  // ── Greedily parse filter tokens ──────────────────────────────────────────
  // We need to extract gender, price, sharing tokens while the rest
  // stays as location. We test known token values against the filterStr.

  let gender  = ''
  let price   = ''
  let sharing = ''
  let remaining = filterStr

  // Try each known token (longest first to avoid partial matches)
  const allTokens = [
    ...Object.values(PRICE_TO_SLUG),    // "5000-to-8000", "above-12000", etc.
    ...Object.values(GENDER_TO_SLUG),   // "boys", "girls", "coed"
    ...Object.values(SHARING_TO_SLUG),  // "single", "double", "triple"
  ].sort((a, b) => b.length - a.length) // longest first

  for (const token of allTokens) {
    const regex = new RegExp(`(^|-)${token}(-|$)`)
    if (regex.test(remaining)) {
      if (SLUG_TO_GENDER[token])   gender  = SLUG_TO_GENDER[token]
      if (SLUG_TO_PRICE[token])    price   = SLUG_TO_PRICE[token]
      if (SLUG_TO_SHARING[token])  sharing = SLUG_TO_SHARING[token]
      // Remove the matched token from remaining
      remaining = remaining.replace(regex, (_, pre, post) => (pre && post) ? '-' : '').replace(/^-|-$/g, '')
    }
  }

  return {
    location: slugToLocation(locStr),
    gender,
    price,
    sharing,
  }
}

/**
 * Returns true if a pathname looks like one of our SEO listing slugs.
 * Use this in App.js to decide which route to render.
 */
/**
 * Returns true if a pathname is one of our SEO listing slugs.
 * Listing slugs start with /pg- (dash, not slash).
 * PG detail pages start with /pg/ (slash) — no conflict.
 * Handles both suffixed (/pg-in-x-pppfr) and unsuffixed (/pg-in-x) forms.
 */
export const isListingSlug = (pathname) => {
  return pathname.startsWith('/pg-')
}